import { useState, useEffect } from "react";
import {
  ChartLineUp, Queue, IdentificationCard, UsersThree, SignOut,
  ShieldCheck, Gear, ClockCounterClockwise, List, X, ChartBar, CurrencyCircleDollar, Crown, MagnifyingGlass, TreeStructure, Broadcast, Pulse, GitFork, Radio, Receipt
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_admin-dashboard-flux/artifacts/ef1ryefo_The%20Gowda%E2%80%99s%20Group.png";

const masterNavItems = [
  { id: "overview", label: "Overview", icon: ChartLineUp },
  { id: "analytics", label: "Analytics", icon: ChartBar },
  { id: "user-lookup", label: "User Lookup", icon: MagnifyingGlass },
  { id: "withdrawals", label: "Withdrawals", icon: Queue },
  { id: "tds-report", label: "TDS Report", icon: Receipt },
  { id: "gst-report", label: "GST Report", icon: IdentificationCard },
  { id: "mt5-approvals", label: "MT5 Approvals", icon: CurrencyCircleDollar },
  { id: "tradingview", label: "TradingView", icon: ChartLineUp },
  { id: "bridge-master", label: "MT5 Bridge", icon: Broadcast },
  { id: "signal-dispatch", label: "Signal Dispatch", icon: Radio },
  { id: "binary-tree", label: "Binary Tree", icon: GitFork },
  { id: "distributors", label: "Distributors", icon: Crown },
  { id: "referral-settings", label: "Referral Settings", icon: TreeStructure },
  { id: "employees", label: "Employees", icon: UsersThree },
  { id: "audit-log", label: "Audit Log", icon: ClockCounterClockwise },
  { id: "system-status", label: "System Status", icon: Pulse },
  { id: "settings", label: "Settings", icon: Gear },
];

const slaveNavItems = [
  { id: "user-lookup", label: "User Lookup", icon: MagnifyingGlass },
  { id: "withdrawals", label: "Withdrawals", icon: Queue, perm: "withdrawals" },
  { id: "mt5-approvals", label: "MT5 Approvals", icon: CurrencyCircleDollar, perm: "mt5" },
  { id: "bridge-master", label: "MT5 Bridge", icon: Broadcast, perm: "mt5" },
  { id: "distributors", label: "Distributors", icon: Crown },
  { id: "settings", label: "Settings", icon: Gear },
];

export default function Sidebar({ activeTab, setActiveTab }) {
  const { user, logout } = useAuth();
  const isMaster = user?.role === "master_admin";
  const userPerms = user?.permissions || ["withdrawals", "kyc", "mt5"];
  const navItems = isMaster
    ? masterNavItems
    : slaveNavItems.filter((item) => !item.perm || userPerms.includes(item.perm));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on tab change
  useEffect(() => { setMobileOpen(false); }, [activeTab]);

  // Close on escape key
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const sidebarContent = (
    <>
      <div className="flex items-center justify-between p-5 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="VeloxAlgoTech LLP" className="w-11 h-11" />
          <span
            className="text-xs font-black tracking-[0.08em] leading-[1.1] flex flex-col"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="brand-name-sidebar"
          >
            <span style={{ color: '#00b4d8' }}>Velox</span>
            <span className="text-white">AlgoTech</span>
          </span>
        </div>
        <button
          onClick={() => setMobileOpen(false)}
          className="md:hidden p-1 rounded-md text-[#A1A1AA] hover:text-white hover:bg-white/5"
          data-testid="close-mobile-sidebar"
        >
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] px-2 mb-3">Navigation</p>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm mb-1 transition-all duration-200 ${
                isActive
                  ? "bg-[rgba(0,229,255,0.1)] text-[#00E5FF]"
                  : "text-[#A1A1AA] hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon size={18} weight={isActive ? "duotone" : "regular"} />
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-2 px-2 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
              <ShieldCheck size={14} className="text-[#00E5FF]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white truncate">{user?.name || "Admin"}</p>
            <p className="text-[10px] text-[#A1A1AA] truncate">{isMaster ? "Master Admin" : "Slave Admin"}</p>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#A1A1AA] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-all duration-200"
        >
          <SignOut size={16} />
          Logout
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        data-testid="mobile-menu-button"
        className="fixed top-3 left-3 z-50 p-2 rounded-md bg-[#121212] border border-white/10 md:hidden"
      >
        <List size={20} className="text-[#A1A1AA]" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen w-[260px] flex flex-col border-r border-white/10 z-50 transform transition-transform duration-300 md:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ background: "#0A0A0A" }}
        data-testid="mobile-sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex fixed left-0 top-0 h-screen w-[220px] flex-col border-r border-white/10 z-40"
        style={{ background: "#0A0A0A" }}
        data-testid="sidebar"
      >
        {sidebarContent}
      </aside>
    </>
  );
}
